import { PriceFormatConfigOptions } from '../products/types';
import { formatPrice } from '../../../common/formatting/formattingService';
import { get } from '../../../common/utils/objectUtils';
import { Dictionary } from '../../../../types/globals';
import {
    PDPApiProductData,
    ProductDetail,
    SimplesObjectKey,
    Simple,
    Size,
    DeliveryInfo,
    ColorVariants,
    ApiColorVariants,
    RawSimples,
} from './types';
import { getFabricSampleCountries } from './fabricSample';

export const reduceDeliveryInformation = (simple: Simple): DeliveryInfo | null => {
    const deliveryMinDays = get(simple, 'delivery_info.delivery_min_days');
    const deliveryMaxDays = get(simple, 'delivery_info.delivery_max_days');
    const label = get(simple, 'delivery_info.label', '');
    const detailedText = get(simple, 'delivery_info.detailed_text', '');
    const isDeliveryBeforeChristmas = get(simple, 'delivery_info.enable_xmas_delivery', false);
    let isGenericInfo = false;
    if (!deliveryMinDays && deliveryMinDays !== 0 && !deliveryMaxDays && deliveryMaxDays !== 0) {
        isGenericInfo = true;
    }
    if (isGenericInfo && detailedText === '') {
        return null;
    }
    const deliveryType = get(simple, 'delivery_info.delivery_type');
    return {
        deliveryMaxDays,
        deliveryMinDays,
        detailedText,
        isDeliveryBeforeChristmas,
        isGenericInfo,
        label,
        twoManHandling: ['2MHNoTime', '2MH'].includes(deliveryType),
    };
};

/* eslint-disable @typescript-eslint/camelcase, camelcase */
export const reduceRatings = (ratingsFromApi: { average_score?: number; total_ratings?: number }) => {
    if (!ratingsFromApi || !ratingsFromApi.average_score || !ratingsFromApi.total_ratings) {
        return undefined;
    }
    return {
        average: ratingsFromApi.average_score,
        count: ratingsFromApi.total_ratings,
    };
};
/* eslint-enable @typescript-eslint/camelcase, camelcase */

export const getSimplesArray = (simples: SimplesObjectKey) => {
    const simplesArray: Array<Simple> = [];
    Object.keys(simples).forEach(sku => {
        simplesArray.push({ ...simples[sku].meta, sku });
    });
    return simplesArray;
};

export const sortSimplesByAvailability = (simples: SimplesObjectKey) => {
    const simplesArray = getSimplesArray(simples);
    return simplesArray.sort((a: Simple, b: Simple) => {
        if (!a.available && b.available !== a.available) {
            return 1;
        }
        if (!b.available && b.available !== a.available) {
            return -1;
        }
        return 0;
    });
};

export const sortSimplesByPrice = (simples: Simple[]) => {
    return simples.sort((a: Simple, b: Simple) => (a.price! >= b.price! ? 1 : -1));
};

export const mapSizeToSimple = (simples: SimplesObjectKey): Size[] => {
    const sortedSimples = sortSimplesByPrice(getSimplesArray(simples));
    return sortedSimples.map(simple => {
        return {
            filterSize: simple.size,
            isAvailable: simple.available,
            isVisible: simple.is_visible,
            simple: simple.sku,
        };
    });
};

export const belongProductGeneralSKU = (generalSKU: string, variantSimpleSKU = '') =>
    variantSimpleSKU.trim().split('-')[0] === generalSKU.trim().split('-')[0];

/**
 * simple sku = DEQxxxxxxxxx-xxxxxx to => config sku DEQxxxxxxx
 */
export const getConfigSkuFromSimpleSku = (simpleSku: string) => {
    const index = simpleSku.indexOf('-');
    if (index === -1) {
        return '';
    }
    return simpleSku.substring(0, index);
};

export const getSimpleColorVariants = (simples: Simple, generalSKU: string): ColorVariants[] => {
    return (simples.color_variants || []).map(colorVariant => ({
        colorImageUrl: get(colorVariant, 'images.original', ''),
        colorImagePath: get(colorVariant, 'images.image_path', ''),
        colorImageTag: get(colorVariant, 'images.tag', ''),
        fabricSampleName: colorVariant.fabric_sample_name || '',
        fabricSampleImage: colorVariant.fabric_sample_image || '',
        enableFabricSample: colorVariant.enable_fabric_sample,
        isSelected: belongProductGeneralSKU(generalSKU, colorVariant.sku),
        pdpUrl: colorVariant.pdp_url,
        sku: colorVariant.sku,
        configSku: getConfigSkuFromSimpleSku(colorVariant.sku),
    }));
};

export const mapApiColorVariantsToReducerColorVariants = (
    apiColorVariants: ApiColorVariants[],
    generalSKU: string
): ColorVariants[] => {
    return (apiColorVariants || []).map(colorVariant => ({
        colorImageUrl: get(colorVariant, 'images.original', ''),
        colorImagePath: get(colorVariant, 'images.image_path', ''),
        colorImageTag: get(colorVariant, 'images.tag', ''),
        fabricSampleName: colorVariant.fabric_sample_name || '',
        fabricSampleImage: colorVariant.fabric_sample_image || '',
        enableFabricSample: colorVariant.enable_fabric_sample,
        isSelected: belongProductGeneralSKU(generalSKU, colorVariant.sku),
        pdpUrl: colorVariant.pdp_url,
        sku: colorVariant.sku,
        configSku: getConfigSkuFromSimpleSku(colorVariant.sku),
    }));
};

export const reduceToProductDetail = ({
    pdpData,
    formatOptions,
    cheapestSimple,
}: {
    pdpData: PDPApiProductData;
    formatOptions: PriceFormatConfigOptions;
    cheapestSimple: Simple;
}): ProductDetail => {
    let price = String(cheapestSimple.price);
    let originalPrice = String(cheapestSimple.original_price);

    if (formatOptions) {
        const { countryCode, currency } = formatOptions;
        price = formatPrice(countryCode, currency, cheapestSimple.price);
        originalPrice = formatPrice(countryCode, currency, cheapestSimple.original_price);
    }
    const sku = get(pdpData, 'meta.sku');
    return {
        sku,
        activeSimpleSKU: cheapestSimple.sku,
        name: get(pdpData, 'meta.name', ''),
        sizes: mapSizeToSimple(pdpData.simples),
        additionalInfo: get(pdpData, 'meta.additional_info'),
        isAvailable: cheapestSimple.available,
        selectedSize: cheapestSimple.size,
        blackFriday: get(pdpData, 'meta.black_friday_top_deal'),
        cyberMonday: get(pdpData, 'meta.cyber_monday_top_deal'),
        quantityBadge: get(pdpData, 'meta.quantity_badge_number'),
        uvpText: get(pdpData, 'meta.uvp_text'),
        brand: get(pdpData, 'meta.brand'),
        imageUrl: `${cheapestSimple.image_url}.jpg`,
        category: get(pdpData, 'meta.category'),
        originalPrice,
        numericPrice: cheapestSimple.price,
        price,
        deliveryInfo: reduceDeliveryInformation(cheapestSimple),
        rating: reduceRatings(pdpData.meta.ratings),
        innerDetails: {
            careLabel: cheapestSimple.product_details.pdp_care_label,
            color: cheapestSimple.product_details.pdp_color,
            material: cheapestSimple.product_details.pdp_material,
            details: cheapestSimple.product_details.pdp_details,
            measures: cheapestSimple.product_details.pdp_measures,
        },
        seoData: {
            seoTitle: get(pdpData, 'meta.metadata.title', ''),
            seoDescription: get(pdpData, 'meta.metadata.description', ''),
            availableIn: get(pdpData, 'meta.available_in', {}),
        },
        isBisrEnabled: cheapestSimple.bisr_enabled,
        hasAddonProducts: !!cheapestSimple.has_addon_product,
        isBisrSubscribed: cheapestSimple.is_user_subscribed,
        quantity: cheapestSimple.quantity,
        assemblyServiceCost: null,
        fabricSampleCountries: getFabricSampleCountries(pdpData),
    };
};

export const getPriceFormatOptions = (priceFormatOptions?: Dictionary<any>) => {
    const formatOptions = { countryCode: '', currency: '' };
    if (priceFormatOptions) {
        formatOptions.countryCode = priceFormatOptions.countryCode;
        formatOptions.currency = priceFormatOptions.currency;
    }
    return formatOptions;
};

/**
 * API returns whatever simple passed in the url as the "default simple sku".
 * Sometimes this simple is invalid. So this functions verifies that the simple
 * exists and if not returns the cheapest simple.
 * @param defaultSku
 * @param simples
 */
export const getCheapestSimple = (defaultSku: string, simples: RawSimples) => {
    if (simples[defaultSku]) {
        return { ...simples[defaultSku].meta, sku: defaultSku };
    }

    const simplesArray: Array<Simple> = [];

    Object.keys(simples).forEach(sku => {
        simplesArray.push({ ...simples[sku].meta, sku });
    });

    // Sort SimplesArray by price in ascending order
    const sortedSimples = simplesArray.sort((a: Simple, b: Simple) => (a.price! >= b.price! ? 1 : -1));
    const cheapestSku = sortedSimples[0].sku;
    return { ...simples[cheapestSku].meta, sku: cheapestSku };
};
